import { EmployeeAvatar } from "@/components/employee/EmployeeAvatar.tsx";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import { Card, FontAwesomeIcon, Page, Stack, Text, Title } from "@skailer-com/uikit";
import { Link } from "react-router-dom";
import { useEffectOnce } from "react-use";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { Trans, useTranslation } from "react-i18next";

const accountSections = [
  {
    titleKey: "common:security",
    captionKey: "core:account_security_caption",
    icon: "fa-lock",
    to: "/account-settings/security",
  },
  {
    titleKey: "core:account_localisation",
    captionKey: "core:account_localisation_caption",
    icon: "fa-earth-americas",
    to: "/account-settings/localisation",
  },
  {
    titleKey: "common:theme",
    captionKey: "core:account_theme_caption",
    icon: "fa-palette",
    to: "/account-settings/theme",
  },
  {
    titleKey: "common:notifications",
    captionKey: "core:account_notifications_caption",
    icon: "fa-bell",
    to: "/account-settings/notifications",
  },
  {
    titleKey: "core:settings_recruitment",
    captionKey: "core:account_recruitment_caption",
    icon: "fa-user-magnifying-glass",
    to: "/account-settings/integrations",
  },
];

export const AccountSettingsDetails = () => {
  const { t } = useTranslation();
  const { data: employee } = useEmployeeMe();
  const { renderEmployeeName } = useFormatter();

  useTitle(t("core:account_overview"));
  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.employees.accountSettingsViewed);
  });

  const profileUrl = employee ? `/employees/${employee.id}` : "/employees";

  return (
    <Page>
      <Page.Header title={t("core:account_overview")} />
      <Page.Content>
        <Stack className={"container-lg mx-auto"} gap={"2xl"}>
          {employee && (
            <Stack items={"center"} gap={"md"}>
              <EmployeeAvatar employee={employee} size={"2xl"} className={"shadow-lg"} />
              <Stack items={"center"}>
                <Title size={"lg"} header={renderEmployeeName(employee)} />
                <Text className={"text-secondary text-lg"}>
                  <Trans
                    i18nKey="core:account_manage_personal_info_description"
                    components={{
                      profile: (
                        <Link className={"text-lg underline hover:no-underline"} to={profileUrl} />
                      ),
                    }}
                  />
                </Text>
              </Stack>
            </Stack>
          )}

          <div className={"grid md:grid-cols-3 gap-4"}>
            {accountSections.map((section) => (
              <Card
                key={section.to}
                icon={section.icon}
                header={t(section.titleKey)}
                caption={t(section.captionKey)}
                to={section.to}
              />
            ))}
            <Card
              icon={"fa-user-pen"}
              header={t("core:settings_personal_info_title")}
              captionSlot={
                <Stack direction={"horizontal"} items={"center"} gap={"xs"}>
                  <Text className={"text-secondary"}>
                    {t("core:settings_personal_info_caption")}
                  </Text>
                  <FontAwesomeIcon icon={"fa-light fa-arrow-right"} className={"text-secondary"} />
                </Stack>
              }
              to={profileUrl}
              variant={"action"}
            />
          </div>
        </Stack>
      </Page.Content>
    </Page>
  );
};
